import React, { useState } from 'react';
import { DollarSign, Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/authService';

const VendorEarnings = () => {
  const [period, setPeriod] = useState('month');

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendorEarnings', period],
    queryFn: async () => {
      const response = await api.get(`/vendor/analytics/earnings?period=${period}`);
      return response.data;
    }
  });

  const earnings = data?.data || {};

  return (
    <>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Earnings</h1>
            <p className="text-sm text-gray-600">Track your revenue and earnings over time</p>
          </div>
          <div className="flex items-center gap-3 mt-4 sm:mt-0">
            <button className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-sm w-full sm:w-auto justify-center">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Period Tabs */}
        <div className="flex space-x-4 border-b border-gray-200 mb-6 overflow-x-auto">
          {[
            { value: 'week', label: 'This Week' },
            { value: 'month', label: 'This Month' },
            { value: 'quarter', label: 'This Quarter' },
            { value: 'year', label: 'This Year' }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setPeriod(tab.value)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors duration-200 ${
                period === tab.value
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-32"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            Failed to load earnings data. Please try again.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Total Earnings</p>
                  <DollarSign className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">
                  ${earnings.total_earnings?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {earnings.start_date} - {earnings.end_date}
                </p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-600">Net Earnings</p>
                  <DollarSign className="w-5 h-5 text-green-500" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-green-600">
                  ${earnings.net_earnings?.toFixed(2) || '0.00'}
                </p>
                <p className="text-xs text-gray-500 mt-1">Your total payout</p>
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6 mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Earnings Breakdown</h2>
              {earnings.earnings_by_service && earnings.earnings_by_service.length > 0 ? (
                <div className="space-y-4">
                  {earnings.earnings_by_service.map((service, index) => (
                    <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 border-b border-gray-100 last:border-0 gap-2">
                      <div>
                        <p className="font-medium text-gray-900">{service.service_name || 'Unknown Service'}</p>
                        <p className="text-sm text-gray-500">{service.bookings_count || 0} bookings</p>
                      </div>
                      <p className="text-lg font-semibold text-gray-900">
                        ${service.total_earnings?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No earnings data available for this period</p>
              )}
            </div>
          </>
        )}
    </>
  );
};

export default VendorEarnings;
